import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Redirect } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteRedirect } from '../hooks/use-redirect-mutations';

export interface DeleteRedirectDialogProps {
  storeId: string;
  redirect: Redirect | null;
  onOpenChange: (open: boolean) => void;
}

export function DeleteRedirectDialog({
  storeId,
  redirect,
  onOpenChange,
}: DeleteRedirectDialogProps) {
  const { t } = useTranslation(['redirects', 'common']);
  const remove = useDeleteRedirect(storeId);
  const message = useErrorMessage();

  return (
    <AlertDialog
      open={redirect !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('redirects:delete.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('redirects:delete.description', {
              from: redirect?.fromPath ?? '',
              to: redirect?.toPath ?? '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(remove.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (redirect) {
                remove.mutate(redirect.id, { onSuccess: () => onOpenChange(false) });
              }
            }}
          >
            {remove.isPending ? t('redirects:delete.submitting') : t('redirects:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
